/**
 * WebSignService - Simplified integration with edatalia-websign
 * Handles signature capture using WebSign library
 */

import 'edatalia-websign';
import { i18n } from '../utils/i18n.js';
import { isMobile } from '../utils/deviceDetect.js';
import { sanitizeLatin1, sanitizeObjectLatin1 } from '../utils/sanitizeLatin1.js';

export class WebSignService {
  constructor() {
    this.webSign = null;
    this.isInitialized = false;
  }

  /**
   * Initialize WebSign library
   */
  async initialize() {
    this.isInitialized = typeof window.WebSign !== 'undefined';
    
    if (!this.isInitialized) {
      console.error('WebSign not available on window object');
    }
    
    return this.isInitialized;
  }

  /**
   * Capture signature for a signer
   * @param {Object} signerData - Signer information
   * @param {File|Blob} pdfFile - PDF file to sign
   * @param {Object} config - Configuration from panel
   * @returns {Promise<Object>} Signature result
   */
  async captureSignature(signerData, pdfFile, config = {}) {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      if (!pdfFile) {
        throw new Error('No PDF file provided');
      }

      // Convert Blob to File if needed
      const inputFile = pdfFile instanceof File 
        ? pdfFile 
        : new File([pdfFile], 'document.pdf', { type: 'application/pdf' });

      // Build WebSign options
      const options = await this.buildWebSignOptions(signerData, config);

      // Create WebSign instance and capture signature
      return await this.launchWebSign(inputFile, options);

    } catch (error) {
      throw error;
    }
  }

  /**
   * Build WebSign configuration options
   */
  async buildWebSignOptions(signerData, config) {
    
    // Get assets path from settings (default: /assets)
    const assetsPath = config.settings?.assetsPath || '/assets';
    const ebpPath = `${assetsPath}/ebp`;
    const certificatePath = `${assetsPath}/certificate`;
    
    // Check if EBP assets are available (only if ebpText is provided)
    let ebpAssetsAvailable = false;
    if (signerData.ebpText) {
      ebpAssetsAvailable = await this.checkEbpAssets(ebpPath);
      if (!ebpAssetsAvailable) {
        console.warn('EBP assets not found. Signature will proceed without EBP templates.');
      }
    }
    
    // Check if signature background image exists for certificate type
    let signatureBackgroundB64 = null;
    if (signerData.signatureType === 'certificate') {
      signatureBackgroundB64 = await this.loadSignatureBackground(certificatePath);
    }

    // Build widget configuration
    const widget = {
      type: signerData.widget?.type,
      width: signerData.widget?.width || 160,
      height: signerData.widget?.height || 100,
      gapX: signerData.widget?.gapX || 0,
      gapY: signerData.widget?.gapY || 0,
      customText: signerData.widget?.customText?.map(item => ({
        ...item,
        text: sanitizeLatin1(item.text)
      })) || [
        {
          text: 'Documento firmado digitalmente',
          fontSize: 6
        },
        {
          text: 'Fecha: ##TIMESTAMP##',
          fontSize: 4
        }
      ]
    };

    // Add type-specific properties
    if (widget.type === 'field') {
      widget.fieldName = signerData.widget?.fieldName || '';
    } else if (widget.type === 'fixed') {
      widget.x = signerData.widget?.x ?? 50;
      widget.y = signerData.widget?.y ?? 50;
      widget.page = signerData.widget?.page ?? 1;
    } else {
      // Float type requires text property
      widget.text = sanitizeLatin1(signerData.widget?.text || '');
      if (!widget.text) {
        console.warn('Float widget type requires text property for text search');
      }
    }

    // Add signature background image if available
    if (signatureBackgroundB64) {
      widget.b64Image = signatureBackgroundB64;
    }

    // Build wacom configuration with EBP templates and screensavers (only if assets are available)
    const wacomConfig = (signerData.ebpText && ebpAssetsAvailable) ? {
      enableScreenSaver: false,
      blackAndWhiteScreenSaver: this.getAssetPath('blackAndWhiteScreenSaver.png', ebpPath),
      colorScreenSaver: this.getAssetPath('colorScreenSaver.png', ebpPath),
      enableEbp: true,
      blackAndWhiteEbp: this.getAssetPath('blackAndWhiteEbp.bin', ebpPath),
      colorEbp: this.getAssetPath('colorEbp.bin', ebpPath),
      varEbp: this.buildEbpVariables(signerData.ebpText),
      blackAndWhiteInitialEbp: this.getAssetPath('blackAndWhiteInitialEbp.bin', ebpPath),
      colorInitialEbp: this.getAssetPath('colorInitialEbp.bin', ebpPath),
      varInitialEbp: sanitizeLatin1('Titulo=Título inicial|||Subtitulo=Subtítulo inicial|||Nombre=Edatalia|||TextoPrevio=Estoy de acuerdo con el documento a firmar'),
      enableInitialEbp: false
    } : undefined;

    // Signature container size (smaller on mobile)
    const containerWidth = isMobile ? '400px' : '700px';
    const containerHeight = isMobile ? '228px' : '400px';

    return {
      enableBridge: config.settings?.enableBridge,
      enforceBridge: config.settings?.enforceBridge,
      autoDownload: false,
      signatureType: signerData.signatureType || 'dtu',
      signatureEnvironment: config.settings?.signatureEnvironment || 'SANDBOX',
      signatureServiceUrl: config.settings?.signatureServiceUrl,
      bearerToken: config.settings?.bearerToken || '',
      encCertKey: config.settings?.encCertKey || null,
      jsUrl: 'https://cdn.jsdelivr.net/npm/edatalia-websign@3.4.2',
      signatureContainerWidth: containerWidth,
      signatureContainerHeight: containerHeight,
      
      // Widget configuration
      widget,

      // Wacom configuration (if using EBP templates)
      ...(wacomConfig ? { wacom: wacomConfig } : {}),

      // Minimum points required to sign (only if defined in settings)
      ...(config.settings?.minPointsRequiredToSign !== undefined
        ? { minPointsRequiredToSign: config.settings.minPointsRequiredToSign }
        : {}),

      // Mouse EBP (only if defined in settings)
      ...(config.settings?.mouseEBP !== undefined
        ? { mouseEBP: config.settings.mouseEBP }
        : {}),

      // Options
      options: {
        includeTimestamp: signerData.tspActivate || false,
        includeOCSP: signerData.ocspActivate || false
      },

      // TSP configuration (only if activated)
      ...(signerData.tspActivate ? {
        timestampProvider: {
          url: signerData.tspUrl || '',
          user: signerData.tspUser || '',
          password: signerData.tspPassword || ''
        }
      } : {}),

      // OCSP configuration (only if activated)
      ...(signerData.ocspActivate ? {
        ocspProvider: {
          url: signerData.ocspUrl || '',
          user: signerData.ocspUser || '',
          password: signerData.ocspPassword || ''
        }
      } : {})
    };
  }

  /**
   * Get asset path for EBP templates and screensavers
   * @param {string} filename - The filename to append
   * @param {string} basePath - Base path from settings (default: /assets/ebp)
   */
  getAssetPath(filename, basePath = '/assets/ebp') {
    // Ensure basePath doesn't end with slash
    const cleanPath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
    return `${cleanPath}/${filename}`;
  }

  /**
   * Load signature background image as base64 (optional)
   * @param {string} certificatePath - Path to certificate assets folder
   * @returns {Promise<string|null>} Base64 string or null if not found
   */
  async loadSignatureBackground(certificatePath) {
    const cleanPath = certificatePath.endsWith('/') ? certificatePath.slice(0, -1) : certificatePath;
    const url = `${cleanPath}/signatureBackground.jpg`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.startsWith('image/')) return null;
      
      const blob = await response.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          // Remove data URL prefix to get pure base64
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }

  /**
   * Check if EBP assets exist (non-blocking)
   * @param {string} ebpPath - Base path to EBP assets
   * @returns {Promise<boolean>} True if all assets exist
   */
  async checkEbpAssets(ebpPath) {
    const requiredFiles = [
      'blackAndWhiteScreenSaver.png',
      'colorScreenSaver.png',
      'blackAndWhiteEbp.bin',
      'colorEbp.bin',
      'blackAndWhiteInitialEbp.bin',
      'colorInitialEbp.bin'
    ];

    try {
      const checks = await Promise.all(
        requiredFiles.map(async (file) => {
          const path = this.getAssetPath(file, ebpPath);
          try {
            const response = await fetch(path, { method: 'HEAD' });
            return response.ok;
          } catch {
            return false;
          }
        })
      );

      return checks.every(result => result === true);
    } catch {
      return false;
    }
  }

  /**
   * Build EBP variables string from ebpText object.
   * Sanitizes values to Latin1 to prevent btoa() InvalidCharacterError.
   */
  buildEbpVariables(ebpText) {
    if (!ebpText || typeof ebpText !== 'object') {
      return '';
    }

    return Object.entries(ebpText)
      .map(([key, value]) => `${sanitizeLatin1(key)}=${sanitizeLatin1(value)}`)
      .join('|||');
  }

  /**
   * Launch WebSign and handle events
   */
  launchWebSign(inputFile, options) {
    return new Promise((resolve, reject) => {
      try {
        // Sanitize all string values in options to prevent btoa() InvalidCharacterError
        const sanitizedOptions = sanitizeObjectLatin1(options);

        // Clean up the web-sign div content before creating new instance
        const webSignDiv = document.getElementById('web-sign');
        if (webSignDiv) {
          webSignDiv.innerHTML = '';
        }
        const webSign = new window.WebSign(inputFile, sanitizedOptions, async (event) => {
          switch (event.type) {
            case 'cancel':
              resolve({
                success: false,
                cancelled: true,
                error: 'Signature cancelled by user'
              });
              break;

            case 'signed':
              const pdfBlob = event.data;
              const signedFile = new File([pdfBlob], 'signed.pdf', {
                type: 'application/pdf',
                lastModified: Date.now()
              });

              resolve({
                success: true,
                signedFile: signedFile,
                timestamp: new Date().toISOString()
              });
              break;

            case 'error':
              const errorMessage = event.error || i18n.t('error.unknown');
              
              // Short signature: ignore, let user retry
              if (errorMessage == "La firma debe ser más larga" || errorMessage == "La firma debe ser mas larga") {
                break;
              }

              // Bridge not running: ignore if not enforced, reject if enforced
              if (errorMessage == "bridge not running") {
                if (options.enforceBridge) {
                  reject(new Error(i18n.t('error.bridgeRequired')));
                }
                break;
              }

              reject(new Error(errorMessage));
              break;

            default:
              // Unknown event type
          }
        });

        this.webSign = webSign;
      } catch (error) {
        console.error('Error creating WebSign instance:', error);
        reject(error);
      }
    });
  }


}
